import type { PostPublic, PostMutationOptions } from '@repo/types/posts';
import type { ReactNode } from 'react';

import { useDeletePost } from '@repo/api/post/useDeletePost';
import { baseErrorMessages } from '@repo/lib/messages/baseErrorMessages';
import { BookText } from 'lucide-react';
import { useEffect, useMemo } from 'react';

import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogBody,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/dialog';
import { FeaturedIcon } from '@/components/ui/featured-icon';
import { toasterMessageHandler } from '@/lib/toaster-message-handler';

/**
 * Props for the DeletePost component.
 * @property {PostPublic[] | null} [posts] - Array of posts to delete.
 * @property {boolean} open - Whether the dialog is open.
 * @property {(value: boolean) => void} onOpenChange - Callback to change dialog open state.
 * @property {PostMutationOptions} [options] - The options for the post.
 * @property {() => void} [onDeleted] - The function to call when the post is deleted.
 */
interface DeletePostProps {
  posts?: PostPublic[] | null;
  open: boolean;
  onOpenChange: (value: boolean) => void;
  options?: PostMutationOptions;
  onDeleted?: () => void;
}

export default function DeletePost(deletePostProps: DeletePostProps) {
  const { posts, open, onOpenChange, options, onDeleted } = deletePostProps;
  const isSingle: boolean = posts?.length === 1;

  const { mutateAsync, reset } = useDeletePost(options);

  useEffect(() => {
    if (open) {
      reset();
    }
  }, [open, reset]);

  const onSubmit = async () => {
    if (!posts) return;
    try {
      await Promise.all(posts.map((element) => mutateAsync(element.id)));
      onDeleted?.();
      onClose();
    } catch (error) {
      toasterMessageHandler(baseErrorMessages, error as Error);
    }
  };

  const onClose = () => {
    onOpenChange(false);
  };

  const header = useMemo<string>(() => {
    if (isSingle) {
      return 'Usunąć Post ?';
    } else {
      return `Usunąć ${posts?.length} posty?`;
    }
  }, [isSingle, posts]);

  const content = useMemo<ReactNode>(() => {
    if (isSingle) {
      return (
        <p className={'text-text-secondary'}>
          Czy na pewno chcesz usunąć post{'  '}
          <b>{posts?.[0]?.name}</b>?
        </p>
      );
    } else {
      return <p className={'text-text-secondary'}>Czy na pewno chcesz usunąć wybrane posty ?</p>;
    }
  }, [isSingle, posts]);

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className={'sm:max-w-96'}>
        <AlertDialogBody>
          <AlertDialogHeader>
            <FeaturedIcon size={'lg'}>
              <BookText />
            </FeaturedIcon>
            <div className={'flex min-w-0 grow flex-col gap-0.5'}>
              <AlertDialogTitle>{header}</AlertDialogTitle>
              <AlertDialogDescription className={'text-text-tertiary text-xs'}>Usuń wybrane posty.</AlertDialogDescription>
            </div>
          </AlertDialogHeader>
          <div className={'flex flex-col gap-3 overflow-auto p-4'}>
            {content}
            {!isSingle && posts && (
              <div className={'flex flex-col gap-3'}>
                <h5 className={'text-text-tertiary text-sm font-medium'}>Posty:</h5>
                <div className={'flex flex-wrap gap-2'}>
                  {posts.map(({ name, id }) => {
                    return (
                      <Badge key={id} size={'sm'} variant={'muted'}>
                        {name}
                      </Badge>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </AlertDialogBody>
        <AlertDialogFooter className={'border-border-primary dark:bg-card bg-accent border-t'}>
          <AlertDialogCancel variant={'tertiary'} onClick={onClose}>
            Zamknij
          </AlertDialogCancel>
          <AlertDialogAction onClick={onSubmit}>Potwierdź</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
